import { formatDateTime } from '@kominiarz/shared';
import { Bell, BellOff, BellRing, CircleCheck, Smartphone, Trash2 } from 'lucide-react';
import { useCurrentUser } from '../../auth/useAuth';
import { usePushDevices, usePushStatus } from '../../lib/push';
import { errorMessage } from '../../lib/api';
import { Alert } from '../Alert';
import { Button, ButtonLink } from '../Button';
import { Spinner } from '../Loading';
import { Section } from '../Section';
import { usePushActions } from './usePushActions';
import styles from './push.module.css';

/** Settings: notifications on this device, the test notification and the list of devices. */
export function DeviceNotificationsSection() {
  const user = useCurrentUser();
  const status = usePushStatus();
  const { enable, disable, test, remove } = usePushActions();
  const state = status.data?.status;
  const devices = usePushDevices(state === 'on' || state === 'off' || state === 'denied');

  return (
    <Section
      title="Powiadomienia na tym urządzeniu"
      description="Przypomnienia o wizytach i poranny plan dnia przychodzą na każde urządzenie, na którym je włączysz."
    >
      {status.isPending && <Spinner />}
      {status.isError && <Alert kind="error">{errorMessage(status.error)}</Alert>}

      {state === 'ios-needs-install' && (
        <Alert kind="info" title="Najpierw dodaj aplikację do ekranu głównego">
          <p>
            Na iPhonie i iPadzie powiadomienia działają tylko w aplikacji otwieranej z ikony na ekranie głównym (iOS 16.4
            lub nowszy).
          </p>
          <p className={styles.alertAction}>
            <ButtonLink to="/instalacja" variant="secondary" icon={<Smartphone aria-hidden="true" />}>
              Jak dodać aplikację
            </ButtonLink>
          </p>
        </Alert>
      )}
      {state === 'unsupported' && (
        <Alert kind="info">
          Ta przeglądarka nie obsługuje powiadomień. Na telefonie z Androidem użyj Chrome, na komputerze Chrome, Edge
          albo Firefoksa.
        </Alert>
      )}
      {state === 'server-off' && (
        <Alert kind="info" title="Powiadomienia nie są jeszcze uruchomione">
          {user.role === 'admin'
            ? 'Na serwerze brakuje kluczy VAPID. Instrukcja jest w README, w części o powiadomieniach.'
            : 'Na serwerze nie skonfigurowano jeszcze powiadomień. Daj znać kierownikowi.'}
        </Alert>
      )}
      {state === 'denied' && (
        <Alert kind="warning" title="Powiadomienia są zablokowane">
          <p>
            Zgoda na powiadomienia została wcześniej odrzucona. Zezwól na nie w ustawieniach przeglądarki (ikona kłódki
            obok adresu strony → Powiadomienia) albo w ustawieniach telefonu, a potem sprawdź ponownie.
          </p>
          <p className={styles.alertAction}>
            <Button variant="secondary" onClick={() => void status.refetch()}>
              Sprawdź ponownie
            </Button>
          </p>
        </Alert>
      )}
      {state === 'off' && status.data?.publicKey && (
        <div className={styles.block}>
          <p>Na tym urządzeniu powiadomienia są wyłączone.</p>
          <Button
            variant="primary"
            size="lg"
            icon={<Bell aria-hidden="true" />}
            disabled={enable.isPending}
            onClick={() => enable.mutate(status.data.publicKey!)}
          >
            Włącz powiadomienia
          </Button>
          <p className={styles.hint}>Przeglądarka zapyta o zgodę – wybierz „Zezwalaj”.</p>
        </div>
      )}
      {state === 'on' && (
        <div className={styles.block}>
          <p className={styles.on}>
            <CircleCheck aria-hidden="true" />
            Powiadomienia są włączone na tym urządzeniu.
          </p>
          <div className={styles.buttons}>
            <Button variant="secondary" icon={<BellRing aria-hidden="true" />} disabled={test.isPending} onClick={() => test.mutate()}>
              Wyślij testowe powiadomienie
            </Button>
            <Button variant="secondary" icon={<BellOff aria-hidden="true" />} disabled={disable.isPending} onClick={() => disable.mutate()}>
              Wyłącz na tym urządzeniu
            </Button>
          </div>
        </div>
      )}

      {devices.data && devices.data.length > 0 && (
        <div className={styles.block}>
          <h3 className={styles.subTitle}>Twoje urządzenia z powiadomieniami</h3>
          <ul className={styles.devices}>
            {devices.data.map((d) => (
              <li key={d.id} className={styles.device}>
                <span className={styles.deviceText}>
                  <span className={styles.deviceName}>
                    {d.label}
                    {d.current && ' – to urządzenie'}
                  </span>
                  <span className={styles.deviceMeta}>
                    {d.lastSuccessAt ? `Ostatnie powiadomienie: ${formatDateTime(d.lastSuccessAt)}` : `Dodano: ${formatDateTime(d.createdAt)}`}
                  </span>
                </span>
                {!d.current && (
                  <Button variant="secondary" icon={<Trash2 aria-hidden="true" />} disabled={remove.isPending} onClick={() => remove.mutate(d.id)}>
                    Usuń
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}
